import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExternalLink, CheckCircle2 } from 'lucide-react';

const PayPalSetupGuide = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="paypal-setup-guide-dialog">
      <DialogHeader>
        <DialogTitle className="text-slate-100 flex items-center gap-2">
          <span className="bg-[#0070BA] text-white px-2 py-0.5 rounded text-xs font-bold">PayPal</span>
          Developer Credentials — Step-by-step
        </DialogTitle>
      </DialogHeader>
      <ScrollArea className="h-[60vh] pr-4">
        <div className="space-y-5 text-sm text-slate-200">
          <p className="text-slate-300">Follow these steps to obtain the four credentials dotbox.ai needs to enable PayPal payments. Start in Sandbox first, then switch to Live once tested.</p>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 1 — Create a PayPal Developer account</h3>
            <ol className="list-decimal ml-6 mt-2 space-y-1.5">
              <li>Go to <a href="https://developer.paypal.com/dashboard/" target="_blank" rel="noopener noreferrer" className="text-red-400 underline inline-flex items-center gap-1">developer.paypal.com/dashboard <ExternalLink className="h-3 w-3" /></a></li>
              <li>Sign in with an existing PayPal account, or create a new Business account if you don&apos;t have one.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 2 — Create a REST API app</h3>
            <ol className="list-decimal ml-6 mt-2 space-y-1.5">
              <li>In the dashboard sidebar click <strong>Apps & Credentials</strong>.</li>
              <li>Toggle the top switch to <strong>Sandbox</strong> (we&apos;ll do Live after testing).</li>
              <li>Click <strong>Create App</strong>. Name it <code className="bg-[#131C3A] px-1 rounded">dotbox-ai</code>. App type: <strong>Merchant</strong>.</li>
              <li>After creation, you&apos;ll see two values — copy them:
                <ul className="list-disc ml-6 mt-1">
                  <li><code className="bg-[#131C3A] px-1 rounded">Client ID</code> → use as <code>PAYPAL_CLIENT_ID</code></li>
                  <li><code className="bg-[#131C3A] px-1 rounded">Secret key</code> → use as <code>PAYPAL_CLIENT_SECRET</code></li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 3 — Configure webhooks</h3>
            <ol className="list-decimal ml-6 mt-2 space-y-1.5">
              <li>On the same app page, scroll to <strong>Webhooks</strong> and click <strong>Add Webhook</strong>.</li>
              <li>Webhook URL: <code className="bg-[#131C3A] px-1 rounded break-all">https://dotbox.ai/api/subscription/paypal-webhook</code></li>
              <li>Subscribe to these events:
                <ul className="list-disc ml-6 mt-1 text-slate-300">
                  <li>BILLING.SUBSCRIPTION.ACTIVATED</li>
                  <li>BILLING.SUBSCRIPTION.CANCELLED</li>
                  <li>BILLING.SUBSCRIPTION.SUSPENDED</li>
                  <li>PAYMENT.SALE.COMPLETED</li>
                  <li>PAYMENT.SALE.REFUNDED</li>
                </ul>
              </li>
              <li>Save. PayPal generates a <strong>Webhook ID</strong> — copy it → use as <code>PAYPAL_WEBHOOK_ID</code></li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 4 — Create a subscription plan</h3>
            <ol className="list-decimal ml-6 mt-2 space-y-1.5">
              <li>In the sidebar, navigate to <strong>Products & Plans</strong>.</li>
              <li>Create a product named <code className="bg-[#131C3A] px-1 rounded">dotbox Pro</code>.</li>
              <li>Add a billing plan: <strong>$24/month</strong>, recurring monthly, USD.</li>
              <li>Copy the resulting Plan ID — use as <code>PAYPAL_PLAN_ID</code> later when wiring user-facing flow.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 5 — Add to dotbox.ai environment variables</h3>
            <p className="mt-2">Add these to your <strong>production</strong> deployment env (Emergent → Deployments → Environment Variables) — and to <code className="bg-[#131C3A] px-1 rounded">backend/.env</code> in preview for testing:</p>
            <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded mt-2 overflow-x-auto"><code>{`PAYPAL_MODE="sandbox"               # switch to "live" after testing
PAYPAL_CLIENT_ID="<from step 2>"
PAYPAL_CLIENT_SECRET="<from step 2>"
PAYPAL_WEBHOOK_ID="<from step 3>"
PAYPAL_PLAN_ID="<from step 4>"`}</code></pre>
          </section>

          <section>
            <h3 className="font-semibold text-slate-100 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Step 6 — Tell me to wire it up</h3>
            <p className="mt-2">Once the env vars are set, ping me and I&apos;ll: install <code>paypalrestsdk</code>, add the checkout + webhook endpoints to the backend, expose the &quot;Pay with PayPal&quot; button on the user subscription page (gated by your feature flag), and write integration tests.</p>
          </section>

          <section className="bg-blue-950/40 border border-blue-800/50 rounded p-3">
            <h3 className="font-semibold text-blue-900">When you&apos;re ready for Live mode</h3>
            <p className="text-blue-200 mt-1">Repeat Steps 2–4 with the <strong>Live</strong> toggle in the PayPal dashboard. You&apos;ll get a separate Live Client ID / Secret / Webhook ID / Plan ID. Update env vars and change <code>PAYPAL_MODE</code> to <code>live</code>.</p>
          </section>
        </div>
      </ScrollArea>
      <DialogFooter>
        <Button onClick={onClose} data-testid="close-paypal-guide">Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default PayPalSetupGuide;
